import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import moment from 'moment';
import 'moment/locale/pt-br';
import performanceService from '../../services/data/performance.service';

moment.locale('pt-br');

const RATING_MAP = {
    '1':  { label: 'Você brilhou!',    color: 'success', icon: '⭐' },
    '0':  { label: 'Esse é o caminho', color: 'info',    icon: '➡️' },
    '-1': { label: 'Pode melhorar',    color: 'warning', icon: '⬇️' },
};

/**
 * Componente reutilizável que lista feedbacks recebidos.
 *
 * Props:
 *  - employeeId  {string}   ID do colaborador cujos feedbacks serão exibidos.
 *                            Quando omitido, exibe os feedbacks do usuário logado.
 *  - onRequest   {function} Callback para abrir o fluxo de "Solicitar Feedback".
 *                            Quando informado, exibe o botão no estado vazio.
 */
export default function FeedbackReceivedList({ employeeId, onRequest }) {
    const toast = useRef(null);

    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasNext, setHasNext] = useState(false);

    useEffect(() => {
        loadFeedbacks(1);
    }, [employeeId]);

    function loadFeedbacks(p) {
        setLoading(true);
        performanceService.listFeedbackReceived(p, employeeId).then((data) => {
            if (p === 1) {
                setFeedbacks(data.results || []);
            } else {
                setFeedbacks((prev) => [...prev, ...(data.results || [])]);
            }
            setHasNext(!!data.next);
            setPage(p);
        }).catch(() => {
            toast.current?.show({ severity: 'warn', summary: 'Ops', detail: 'Erro ao carregar feedbacks', life: 3000 });
        }).finally(() => setLoading(false));
    }

    const ratingCounts = feedbacks.reduce((acc, fb) => {
        acc[fb.rating] = (acc[fb.rating] || 0) + 1;
        return acc;
    }, {});

    return (
        <>
            <Toast ref={toast} position="top-right" />

            {/* Summary chips */}
            {feedbacks.length > 0 && (
                <div className="d-flex gap-2 mb-3 flex-wrap">
                    <span className="badge bg-light text-dark border fw-normal px-3 py-2">
                        Total: <strong>{feedbacks.length}</strong>
                    </span>
                    {Object.entries(ratingCounts).map(([rating, count]) => {
                        const r = RATING_MAP[rating] || {};
                        return (
                            <span
                                key={rating}
                                className={`badge bg-${r.color} bg-opacity-10 text-dark border border-${r.color} fw-normal px-3 py-2`}
                            >
                                {r.icon} {r.label}: <strong>{count}</strong>
                            </span>
                        );
                    })}
                </div>
            )}

            {loading && feedbacks.length === 0 ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                </div>
            ) : feedbacks.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    <FontAwesomeIcon icon={solid('inbox')} size="3x" className="mb-3" />
                    <h5 className="fw-bold">Nenhum feedback recebido ainda</h5>
                    {onRequest ? (
                        <>
                            <p className="small">Solicite feedbacks para começar a crescer!</p>
                            <button className="btn btn-success btn-sm btn-rounded px-3" onClick={onRequest}>
                                Solicitar Feedback
                            </button>
                        </>
                    ) : (
                        <p className="small mb-0">Ainda não há feedbacks para exibir.</p>
                    )}
                </div>
            ) : (
                <>
                    <div className="row g-3">
                        {feedbacks.map((fb) => {
                            const r = RATING_MAP[fb.rating] || {};
                            return (
                                <div key={fb.id} className="col-12 col-md-6 col-xl-4">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className={`card-header border-0 bg-${r.color} bg-opacity-10 p-2`}>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="fw-semibold small mb-0">{fb.created_by_name}</span>
                                                <span className="text-muted small mb-0">
                                                    {moment(fb.created).format('DD/MM/YYYY')}
                                                </span>
                                            </div>
                                        </div>
                                        <div className='d-flex justify-content-center mt-2'>
                                            <Tag severity={r.color} value={`${r.icon} ${r.label}`} />
                                        </div>
                                        <div className="card-body">
                                            <p className="text-dark small mb-0" style={{ whiteSpace: 'pre-wrap' }}>{fb.comment}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {hasNext && (
                        <div className="text-center mt-4">
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => loadFeedbacks(page + 1)}
                                disabled={loading}
                            >
                                {loading ? 'Carregando...' : 'Carregar mais'}
                            </button>
                        </div>
                    )}
                </>
            )}
        </>
    );
}
